(function () {
  const SESSION_VERSION = 1;
  const KLAVIYO_QUIZ_FORM_SELECTOR = '[data-klaviyo-quiz-backend] form';

  function pushQuizEvent(eventName, payload = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      const ctx = window.quizCustomerContext || {};
      window.dataLayer.push({
        event: eventName,
        page_template: ctx.pageTemplate || '',
        isLoggedIn: Boolean(ctx.isLoggedIn),
        customer_id: ctx.customerId ?? null,
        customer_email: ctx.customerEmail ?? null,
        customer_first_name: ctx.customerFirstName ?? null,
        customer_last_name: ctx.customerLastName ?? null,
        ...payload,
      });
    } catch (error) {
      console.warn(`Menopause quiz: dataLayer push failed for "${eventName}"`, error);
    }
  }

  function setNativeInputValue(input, value) {
    const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value');
    if (descriptor?.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }
  }

  function findKlaviyoQuizForm() {
    const form = document.querySelector(KLAVIYO_QUIZ_FORM_SELECTOR);
    const emailInput = form?.querySelector('input[type="email"], input[name="email"]');

    if (form && emailInput) {
      return { form, emailInput };
    }

    return null;
  }

  function waitForKlaviyoQuizForm(timeoutMs = 15000) {
    return new Promise((resolve) => {
      const existing = findKlaviyoQuizForm();
      if (existing) {
        resolve(existing);
        return;
      }

      const container = document.querySelector('[data-klaviyo-quiz-backend]') || document.body;
      let settled = false;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timeoutId);
        resolve(result);
      };

      const observer = new MutationObserver(() => {
        const klaviyoForm = findKlaviyoQuizForm();
        if (klaviyoForm) {
          finish(klaviyoForm);
        }
      });

      observer.observe(container, { childList: true, subtree: true });

      const timeoutId = setTimeout(() => finish(null), timeoutMs);
    });
  }

  async function submitEmailToKlaviyoQuizForm(email) {
    const klaviyoForm = await waitForKlaviyoQuizForm();
    if (!klaviyoForm) {
      console.warn('Menopause quiz: Klaviyo backend form not ready.');
      return false;
    }

    const { form, emailInput } = klaviyoForm;

    setNativeInputValue(emailInput, email);
    emailInput.dispatchEvent(new Event('input', { bubbles: true }));
    emailInput.dispatchEvent(new Event('change', { bubbles: true }));
    emailInput.dispatchEvent(new Event('blur', { bubbles: true }));

    const submitButton =
      form.querySelector('button[type="submit"]') || form.querySelector('input[type="submit"]');

    if (submitButton) {
      submitButton.click();
    } else {
      form.requestSubmit();
    }

    return true;
  }

  class MenopauseQuiz {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.sessionKey = root.dataset.sessionKey || 'menolabs_quiz_session';
      this.sessionTtlHours = Number(root.dataset.sessionTtlHours || 24);
      this.resultsUrl = root.dataset.resultsUrl || '/pages/quiz-recommendations';
      this.homeUrl = root.dataset.homeUrl || '/';
      this.emailRequired = root.dataset.emailRequired === 'true';
      this.minScore = Number(root.dataset.minScore || 1);

      this.modal = root.querySelector('[data-quiz-modal]');
      this.mountModalToBody();
      this.panel = this.modal?.querySelector('.menopause-quiz-modal__panel');
      this.panelDecor = this.panel?.querySelector('[data-quiz-panel-decor]');
      this.header = this.modal?.querySelector('.menopause-quiz-modal__header');
      this.progress = this.modal?.querySelector('[data-quiz-progress]');
      this.progressTrack = this.modal?.querySelector('[data-quiz-progress-track]');
      this.navBack = this.modal?.querySelector('[data-quiz-nav-back]');
      this.navForward = this.modal?.querySelector('[data-quiz-nav-forward]');

      this.stepQuestion = this.modal?.querySelector('[data-quiz-step="question"]');
      this.stepInsight = this.modal?.querySelector('[data-quiz-step="insight"]');
      this.stepEmail = this.modal?.querySelector('[data-quiz-step="email"]');
      this.stepSuccess = this.modal?.querySelector('[data-quiz-step="success"]');

      this.questionIconWrap = this.modal?.querySelector('[data-quiz-question-icon-wrap]');
      this.questionIcon = this.modal?.querySelector('[data-quiz-question-icon]');
      this.questionText = this.modal?.querySelector('[data-quiz-question-text]');
      this.answersContainer = this.modal?.querySelector('[data-quiz-answers]');
      this.insightBody = this.modal?.querySelector('[data-quiz-insight-body]');
      this.emailForm = this.modal?.querySelector('[data-quiz-email-form]');
      this.emailInput = this.modal?.querySelector('[data-quiz-email-input]');
      this.emailError = this.modal?.querySelector('[data-quiz-email-error]');
      this.privacyConsent = this.modal?.querySelector('[data-quiz-privacy-consent]');
      this.citationsList = this.modal?.querySelector('[data-quiz-citations-list]');
      this.citationsDetails = this.modal?.querySelector('[data-quiz-citations-details]');
      this.revealResults = this.modal?.querySelector('[data-quiz-reveal-results]');

      this.decorSteps = (this.panel?.dataset.quizDecorSteps || 'email,success')
        .split(',')
        .map((step) => step.trim())
        .filter(Boolean);

      this.data = this.readData();
      this.questions = (this.data.questions || []).slice().sort((a, b) => Number(a.order) - Number(b.order));
      this.symptoms = this.data.symptoms || [];
      this.copy = this.data.copy || {};

      this.session = this.loadSession() || this.createEmptySession();
      this.currentQuestionIndex = 0;
      this.currentStep = 'question';

      this.bindEvents();
    }

    readData() {
      const script = document.getElementById(`MenopauseQuizData-${this.sectionId}`);
      if (!script) return {};
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Menopause quiz: failed to parse data JSON', error);
        return {};
      }
    }

    mountModalToBody() {
      if (!this.modal || this.modal.parentElement === document.body) return;
      document.body.appendChild(this.modal);
    }

    bindEvents() {
      this.root.querySelectorAll('[data-quiz-open]').forEach((button) => {
        button.addEventListener('click', () => {
          pushQuizEvent('quiz_begin');
          this.openModal();
        });
      });

      this.modal?.querySelectorAll('[data-quiz-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      this.navBack?.addEventListener('click', () => this.goBack());
      this.navForward?.addEventListener('click', () => this.goForward());

      this.emailForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitEmail();
      });

      this.emailInput?.addEventListener('input', () => {
        this.clearEmailErrorIfValid();
      });

      this.revealResults?.setAttribute('href', this.resultsUrl);
      this.revealResults?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goToResults();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.modal?.classList.contains('is-open')) {
          this.closeModal();
        }
      });
    }

    openModal() {
      if (!this.questions.length) {
        console.warn('Menopause quiz: no questions configured in metaobjects.');
        return;
      }

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('menopause-quiz-open');

      // Always start fresh — no mid-quiz resume from sessionStorage.
      this.currentQuestionIndex = 0;
      this.session = this.createEmptySession();
      this.quizStartTime = Date.now();
      this.showQuestion(0);

      this.panel?.focus();
    }

    resetEmailForm() {
      if (this.emailInput) {
        this.emailInput.value = '';
      }

      if (this.privacyConsent) {
        this.privacyConsent.checked = false;
      }

      if (this.emailError) {
        this.emailError.textContent = '';
        this.emailError.hidden = true;
        this.emailError.setAttribute('hidden', '');
      }
    }

    dismissModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('menopause-quiz-open');
      this.resetEmailForm();
      this.hideAllSteps();
      if (this.progress) this.progress.hidden = true;
      this.updatePanelDecor();
      this.updateNav();
    }

    closeModal() {
      if (this.currentStep !== 'success') {
        pushQuizEvent('quiz_abandoned', {
          last_step_name: this.currentStep,
          last_question_index: this.currentQuestionIndex,
          time_in_quiz_ms: this.quizStartTime ? Date.now() - this.quizStartTime : null,
        });
      }
      this.dismissModal();
      this.saveSession({ step: 'closed' });
    }

    createEmptySession() {
      return {
        version: SESSION_VERSION,
        timestamp: Date.now(),
        step: 'question-0',
        answers: [],
        symptomScores: {},
        rankedSymptoms: [],
        email: '',
        emailSubmitted: false,
      };
    }

    loadSession() {
      try {
        const raw = sessionStorage.getItem(this.sessionKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== SESSION_VERSION) return null;

        const ttlMs = this.sessionTtlHours * 60 * 60 * 1000;
        if (Date.now() - Number(parsed.timestamp || 0) > ttlMs) {
          sessionStorage.removeItem(this.sessionKey);
          return null;
        }

        return parsed;
      } catch (error) {
        return null;
      }
    }

    saveSession(updates = {}) {
      const answers = updates.answers || this.session?.answers || [];
      const rankedSymptoms = this.computeRankedSymptoms(answers);

      this.session = {
        ...(this.session || this.createEmptySession()),
        ...updates,
        rankedSymptoms,
        symptomScores: this.computeSymptomScores(answers),
        timestamp: Date.now(),
        version: SESSION_VERSION,
      };
      sessionStorage.setItem(this.sessionKey, JSON.stringify(this.session));
    }

    computeSymptomScores(answers) {
      const scores = {};
      answers.forEach((answer) => {
        if (!answer.symptomHandle) return;
        scores[answer.symptomHandle] = (scores[answer.symptomHandle] || 0) + Number(answer.score || 0);
      });
      return scores;
    }

    computeRankedSymptoms(answers) {
      const symptomMap = Object.fromEntries(this.symptoms.map((symptom) => [symptom.handle, symptom]));
      const bySymptom = {};

      answers.forEach((answer) => {
        if (!answer.symptomHandle) return;
        const existing = bySymptom[answer.symptomHandle];
        if (!existing || Number(answer.score) > Number(existing.score)) {
          bySymptom[answer.symptomHandle] = answer;
        }
      });

      return Object.values(bySymptom)
        .sort((a, b) => {
          if (Number(b.score) !== Number(a.score)) return Number(b.score) - Number(a.score);
          const priorityA = Number(symptomMap[a.symptomHandle]?.resultsPriority ?? 999);
          const priorityB = Number(symptomMap[b.symptomHandle]?.resultsPriority ?? 999);
          return priorityA - priorityB;
        })
        .map((answer) => ({
          symptomHandle: answer.symptomHandle,
          score: Number(answer.score),
          resultsPriority: Number(symptomMap[answer.symptomHandle]?.resultsPriority ?? 999),
        }));
    }

    hideAllSteps() {
      [this.stepQuestion, this.stepInsight, this.stepEmail, this.stepSuccess].forEach((step) => {
        if (step) step.hidden = true;
      });
    }

    updatePanelDecor() {
      if (!this.panelDecor) return;

      const hasImage = Boolean(this.panelDecor.querySelector('img'));
      const shouldShow = hasImage && this.decorSteps.includes(this.currentStep || '');

      this.panelDecor.hidden = !shouldShow;
      this.panelDecor.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }

    setHeaderMode(mode) {
      const modes = ['question', 'insight', 'email', 'success'];

      if (this.header) {
        this.header.classList.remove(...modes.map((item) => `is-${item}`));
        if (mode) this.header.classList.add(`is-${mode}`);
      }

      if (this.panel) {
        this.panel.classList.remove(...modes.map((item) => `is-${item}`));
        if (mode) this.panel.classList.add(`is-${mode}`);
      }
    }

    updateNav() {
      if (!this.navBack || !this.navForward) return;

      this.navBack.hidden = true;
      this.navForward.hidden = true;

      if (this.currentStep === 'question') {
        this.navBack.hidden = this.currentQuestionIndex === 0;
        this.navForward.hidden = true;
        return;
      }

      this.navForward?.classList.remove('is-disabled');

      if (this.currentStep === 'insight') {
        this.navBack.hidden = false;
        this.navForward.hidden = false;
        return;
      }

      if (this.currentStep === 'email') {
        this.navBack.hidden = true;
        this.navForward.hidden = true;
        return;
      }

      if (this.currentStep === 'success') {
        this.navBack.hidden = true;
        this.navForward.hidden = true;
      }
    }

    goBack() {
      const fromStep =
        this.currentStep === 'question' || this.currentStep === 'insight'
          ? `${this.currentStep}-${this.currentQuestionIndex}`
          : this.currentStep;

      if (this.currentStep === 'insight') {
        pushQuizEvent('quiz_back_click', { from_step: fromStep, to_step: `question-${this.currentQuestionIndex}` });
        this.showQuestion(this.currentQuestionIndex);
        return;
      }

      if (this.currentStep === 'question' && this.currentQuestionIndex > 0) {
        const toIndex = this.currentQuestionIndex - 1;
        pushQuizEvent('quiz_back_click', { from_step: fromStep, to_step: `question-${toIndex}` });
        this.currentQuestionIndex = toIndex;
        this.showQuestion(this.currentQuestionIndex);
        return;
      }

      if (this.currentStep === 'email') {
        const lastIndex = this.questions.length - 1;
        pushQuizEvent('quiz_back_click', { from_step: fromStep, to_step: `insight-${lastIndex}` });
        this.currentQuestionIndex = lastIndex;
        this.showInsight(lastIndex);
      }
    }

    goForward() {
      if (this.currentStep === 'question' && this.currentQuestionIndex === 0) {
        return;
      }

      if (this.currentStep === 'insight') {
        this.continueFromInsight();
        return;
      }

      if (this.currentStep === 'email') {
        this.submitEmail();
      }
    }

    renderProgress(activeIndex, mode) {
      if (!this.progressTrack) return;

      this.progress.hidden = false;
      this.progressTrack.innerHTML = '';

      this.questions.forEach((_, index) => {
        const segment = document.createElement('span');
        segment.className = 'menopause-quiz-modal__progress-segment';

        if (mode === 'complete') {
          segment.classList.add('is-complete');
        } else if (index < activeIndex) {
          segment.classList.add('is-complete');
        } else if (index === activeIndex) {
          if (mode === 'insight') {
            segment.classList.add('is-complete');
          } else {
            segment.classList.add('is-active');
          }
        }

        this.progressTrack.appendChild(segment);
      });
    }

    showQuestion(index) {
      const question = this.questions[index];
      if (!question) {
        this.showEmail();
        return;
      }

      this.hideAllSteps();
      this.stepQuestion.hidden = false;
      this.currentStep = 'question';
      this.setHeaderMode('question');
      this.renderProgress(index, 'question');
      this.updatePanelDecor();
      this.saveSession({ step: `question-${index}` });
      this.updateNav();

      if (question.iconUrl) {
        this.questionIconWrap.hidden = false;
        this.questionIcon.src = question.iconUrl;
        this.questionIcon.alt = '';
      } else {
        this.questionIconWrap.hidden = true;
      }

      this.questionText.textContent = question.questionText || '';
      this.answersContainer.innerHTML = '';

      (question.answers || []).forEach((answer, answerIndex) => {
        if (!answer.label) return;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'menopause-quiz-answer';
        button.textContent = answer.label;
        button.addEventListener('click', () => this.selectAnswer(index, answerIndex, answer));
        this.answersContainer.appendChild(button);
      });
    }

    selectAnswer(questionIndex, answerIndex, answer) {
      const question = this.questions[questionIndex];
      if (!question) return;

      pushQuizEvent(`quiz_step_view_${questionIndex + 1}`, {
        question_id: question.id,
        question_index: questionIndex,
        question: question.questionText || '',
        answer_id: answerIndex,
        answer: answer.label || '',
      });

      const existingAnswers = (this.session?.answers || []).filter((item) => item.questionId !== question.id);
      const nextAnswers = existingAnswers.concat([
        {
          questionId: question.id,
          questionHandle: question.handle,
          answerIndex,
          score: Number(answer.score || 0),
          symptomHandle: question.symptomHandle,
        },
      ]);

      this.saveSession({
        answers: nextAnswers,
        step: `insight-${questionIndex}`,
      });

      this.showInsight(questionIndex);
    }

    showInsight(questionIndex) {
      const question = this.questions[questionIndex];
      if (!question) {
        this.continueFromInsight();
        return;
      }

      this.hideAllSteps();
      this.stepInsight.hidden = false;
      this.currentStep = 'insight';
      this.setHeaderMode('insight');
      this.renderProgress(questionIndex, 'insight');
      this.updatePanelDecor();
      this.saveSession({ step: `insight-${questionIndex}` });
      this.updateNav();

      const bodyHtml = this.formatInsightBody(question.insightBody, question.citationRefs || []);
      this.insightBody.innerHTML = bodyHtml;
    }

    formatInsightBody(body, citationRefs) {
      let html = this.parseInsightMarkup(body || '');

      if (citationRefs.length) {
        const superscripts = this.normalizeCitationRefs(citationRefs)
          .map((ref) => `<sup>${ref}</sup>`)
          .join('');
        html = `${html}${superscripts}`;
      }

      return html;
    }

    parseInsightMarkup(body) {
      const trimmed = String(body).trim();
      if (!trimmed) return '';

      if (/<(?:span|strong|b)\b/i.test(trimmed)) {
        return this.sanitizeInsightHtml(trimmed);
      }

      return this.escapeHtml(trimmed)
        .replace(/\*\*(.+?)\*\*/g, '<span class="menopause-quiz-step__insight-accent">$1</span>')
        .replace(/\n/g, '<br>');
    }

    sanitizeInsightHtml(html) {
      const template = document.createElement('template');
      template.innerHTML = html;

      const sanitizeNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.cloneNode();
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return document.createTextNode('');
        }

        const tagName = node.tagName.toLowerCase();
        if (tagName === 'span' || tagName === 'strong' || tagName === 'b') {
          const span = document.createElement('span');
          span.className = 'menopause-quiz-step__insight-accent';
          span.innerHTML = node.innerHTML;
          return span;
        }

        const fragment = document.createDocumentFragment();
        Array.from(node.childNodes).forEach((child) => {
          fragment.appendChild(sanitizeNode(child));
        });
        return fragment;
      };

      const output = document.createElement('div');
      Array.from(template.content.childNodes).forEach((node) => {
        output.appendChild(sanitizeNode(node));
      });

      return output.innerHTML.replace(/\n/g, '<br>');
    }

    escapeHtml(text) {
      const el = document.createElement('div');
      el.textContent = text;
      return el.innerHTML;
    }

    continueFromInsight() {
      const nextIndex = this.currentQuestionIndex + 1;
      if (nextIndex < this.questions.length) {
        this.currentQuestionIndex = nextIndex;
        this.showQuestion(nextIndex);
        return;
      }

      if (this.emailRequired) {
        this.showEmail();
      } else {
        this.showSuccess();
      }
    }

    showEmail() {
      this.hideAllSteps();
      this.stepEmail.hidden = false;
      this.currentStep = 'email';
      this.setHeaderMode('email');
      this.renderProgress(this.questions.length - 1, 'complete');
      this.updatePanelDecor();
      this.saveSession({ step: 'email' });
      this.renderCitationsList();
      this.updateNav();
    }

    normalizeCitationRefs(refs) {
      if (refs == null || refs === '') return [];
      if (Array.isArray(refs)) return refs;
      if (typeof refs === 'number') return [refs];
      if (typeof refs === 'string') {
        return refs
          .split(/[,;]+/)
          .map((value) => value.trim())
          .filter(Boolean);
      }
      return [];
    }

    collectCitationRefs() {
      const refs = new Set();
      (this.session?.answers || []).forEach((answer) => {
        const question = this.questions.find((item) => item.id === answer.questionId);
        this.normalizeCitationRefs(question?.citationRefs).forEach((ref) => {
          const num = Number(ref);
          if (!Number.isNaN(num) && num > 0) refs.add(num);
        });
      });
      return Array.from(refs).sort((a, b) => a - b);
    }

    renderCitationsList() {
      if (!this.citationsDetails) return;
      this.citationsDetails.hidden = false;
    }

    clearEmailErrorIfValid() {
      if (!this.emailError || this.emailError.hidden) return;

      const email = (this.emailInput?.value || '').trim();
      const isValidEmail = Boolean(email) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!isValidEmail) return;

      const invalidMessage = this.copy.emailInvalid || 'Please enter a valid email address';
      if (this.emailError.textContent === invalidMessage) {
        this.emailError.hidden = true;
        this.emailError.textContent = '';
      }
    }

    submitEmail() {
      const email = (this.emailInput?.value || '').trim();
      const consentChecked = this.privacyConsent?.checked;

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        if (this.emailError) {
          this.emailError.hidden = false;
          this.emailError.textContent = this.copy.emailInvalid || 'Please enter a valid email address';
        }
        return;
      }

      if (!consentChecked) {
        if (this.emailError) {
          this.emailError.hidden = false;
          this.emailError.textContent = 'Please agree to the privacy and cookie policies.';
        }
        return;
      }

      if (this.emailError) this.emailError.hidden = true;

      pushQuizEvent('quiz_completed', {
        step_name: 'email',
        filled_email: email,
      });

      submitEmailToKlaviyoQuizForm(email).catch((error) => {
        console.warn('Menopause quiz: Klaviyo submission failed.', error);
      });

      this.saveSession({
        email,
        emailSubmitted: true,
        step: 'success',
      });

      this.resetEmailForm();
      this.showSuccess();
    }

    showSuccess() {
      this.hideAllSteps();
      this.stepSuccess.hidden = false;
      this.currentStep = 'success';
      this.setHeaderMode('success');
      this.renderProgress(this.questions.length - 1, 'complete');
      this.updatePanelDecor();
      this.saveSession({ step: 'success', emailSubmitted: true });
      this.updateNav();
    }

    goToResults() {
      const resultsUrl = this.revealResults?.getAttribute('href') || this.resultsUrl;

      pushQuizEvent('quiz_results_revealed', {
        ranked_symptom_top: this.session?.rankedSymptoms?.[0]?.symptomHandle || null,
      });

      // Persist answers for recommendations only. Do not dismiss the modal here.
      this.saveSession({ emailSubmitted: true, resultsViewed: true });
      window.history.replaceState(null, '', this.homeUrl);
      window.location.assign(resultsUrl);
    }
  }

  // If Back restores a bfcache snapshot with the modal still open after results,
  // close it so the homepage stays clean.
  function handleQuizPageRestore() {
    const root = document.querySelector('[data-menopause-quiz]');
    if (!root) return;

    const sessionKey = root.dataset.sessionKey || 'menolabs_quiz_session';
    let resultsViewed = false;

    try {
      const session = JSON.parse(sessionStorage.getItem(sessionKey) || 'null');
      resultsViewed = Boolean(session?.resultsViewed);
    } catch (error) {
      resultsViewed = false;
    }

    if (!resultsViewed) return;

    document.querySelectorAll('.menopause-quiz-modal.is-open').forEach((modal) => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('menopause-quiz-open');
  }

  function getQuizScrollOffset() {
    const headerHeight = Number.parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    const announcementOffset = document.querySelector('.announcement-bar-section') ? 41 : 0;
    return (Number.isFinite(headerHeight) ? headerHeight : 56) + announcementOffset;
  }

  function scrollToQuizSection(root) {
    if (!root) return;

    const top = root.getBoundingClientRect().top + window.scrollY - getQuizScrollOffset();
    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }

  function shouldScrollToQuiz() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('scroll_to') === 'menopause-quiz') return true;
    if (window.location.hash === '#menopause-quiz') return true;
    return false;
  }

  function handleQuizDeepLink() {
    if (!shouldScrollToQuiz()) return;

    const root = document.getElementById('menopause-quiz') || document.querySelector('[data-menopause-quiz]');
    if (!root) return;

    requestAnimationFrame(() => {
      scrollToQuizSection(root);
    });
  }

  function initMenopauseQuiz() {
    document.querySelectorAll('[data-menopause-quiz]').forEach((root) => {
      if (root.dataset.quizInitialized === 'true') return;
      root.dataset.quizInitialized = 'true';
      new MenopauseQuiz(root);
    });

    handleQuizDeepLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMenopauseQuiz);
  } else {
    initMenopauseQuiz();
  }

  document.addEventListener('shopify:section:load', initMenopauseQuiz);
  window.addEventListener('hashchange', handleQuizDeepLink);
  window.addEventListener('pageshow', handleQuizPageRestore);
})();
